import * as React from 'react';
import * as Sentry from '@sentry/react';
import copy from 'copy-to-clipboard';
import { Check, Copy, MessageCircle, RefreshCw } from 'lucide-react';

import { Button } from '@dashboard/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@dashboard/components/ui/card';
import { cn } from '@dashboard/lib/utils';

/**
 * Open the Crisp live chat. MainLayout loads Crisp, so it's available for
 * in-app (per-route) crashes; fall back to email for a root crash that may
 * happen before the widget has loaded.
 */
function openSupportChat() {
  const crisp = (window as unknown as { $crisp?: { push: (command: unknown[]) => void } }).$crisp;
  if (crisp) {
    crisp.push(['do', 'chat:open']);
    return;
  }
  window.location.href = 'mailto:support@checkoutpage.com';
}

type ErrorFallbackProps = {
  fullScreen: boolean;
  eventId: string;
};

export function ErrorFallback({ fullScreen, eventId }: ErrorFallbackProps) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    copy(eventId);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      role="alert"
      className={cn(
        'flex w-full items-center justify-center p-6',
        fullScreen ? 'min-h-screen' : 'min-h-[60vh]',
      )}
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Something went wrong</CardTitle>
          <CardDescription>Please refresh the page and try again.</CardDescription>
          <p className="text-muted-foreground mt-1 text-sm">
            Our team has been notified. If the problem continues, contact{' '}
            <a href="mailto:support@checkoutpage.com" className="underline">
              support@checkoutpage.com
            </a>
            .
          </p>
        </CardHeader>
        {eventId && (
          <CardContent>
            <p className="text-muted-foreground text-xs font-medium">Reference</p>
            <div className="mt-1 flex items-center gap-2">
              <code className="text-foreground font-mono text-xs break-all">{eventId}</code>
              <Button variant="ghost" size="sm" onClick={handleCopy} className="h-7 shrink-0 px-2">
                {copied ? <Check /> : <Copy />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </CardContent>
        )}
        <CardFooter className="gap-2">
          <Button onClick={() => window.location.reload()}>
            <RefreshCw />
            Refresh page
          </Button>
          <Button variant="outline" onClick={openSupportChat}>
            <MessageCircle />
            Contact support
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

type AppErrorBoundaryProps = {
  children: React.ReactNode;
  /** Center the fallback over the full viewport. Use at the app root. */
  fullScreen?: boolean;
};

/**
 * Tag the Sentry scope so app-root crashes are distinguishable from per-route
 * crashes (and we keep the crashing path) when triaging in the Sentry dashboard.
 */
export function tagErrorBoundaryScope(
  scope: Pick<Sentry.Scope, 'setTag'>,
  { fullScreen, pathname }: { fullScreen: boolean; pathname: string },
) {
  scope.setTag('error_boundary', fullScreen ? 'root' : 'route');
  scope.setTag('route', pathname);
}

export function AppErrorBoundary({ children, fullScreen = false }: AppErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      beforeCapture={scope =>
        tagErrorBoundaryScope(scope, { fullScreen, pathname: window.location.pathname })
      }
      fallback={({ eventId }) => <ErrorFallback eventId={eventId} fullScreen={fullScreen} />}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
